import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { controller } from '../lib/session-controller';
import { fmtClock } from '../lib/metrics';
import { APP_VERSION } from '../version';

export default function Session({ onExit }: { onExit: () => void }) {
  const snap = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  const waveRef = useRef<HTMLDivElement>(null);
  const [elapsed, setElapsed] = useState(0);

  // Session timer.
  useEffect(() => {
    if (!snap.elapsedStartedAt) return;
    const id = setInterval(() => setElapsed((Date.now() - (snap.elapsedStartedAt || Date.now())) / 1000), 500);
    return () => clearInterval(id);
  }, [snap.elapsedStartedAt]);

  // Live waveform — read straight off the recorder each frame, no React churn.
  useEffect(() => {
    if (snap.phase !== 'capturing') return;
    let raf = 0;
    const spans = waveRef.current ? Array.from(waveRef.current.querySelectorAll('span')) : [];
    const loop = () => {
      const levels = controller.recorder.levels();
      spans.forEach((s, i) => {
        (s as HTMLElement).style.transform = `scaleY(${(levels[i] ?? 0.12).toFixed(3)})`;
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [snap.phase]);

  // Device state: mic denied / no questions.
  if (snap.phase === 'mic_denied') {
    return (
      <section className="screen-session">
        <div className="device-state">
          <div className="glyph">🎙️</div>
          <h2>The examiner needs your microphone</h2>
          <p>{snap.micError}</p>
          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-next" onClick={() => { const c = controller.getConfig(); if (c) void controller.start(c); }}>Retry</button>
            <button className="btn-ghost" onClick={onExit}>Back</button>
          </div>
        </div>
        <div className="vstr">v{APP_VERSION}</div>
      </section>
    );
  }

  const asking = snap.phase === 'asking';
  const answering = snap.phase === 'capturing';
  const between = snap.phase === 'between';
  const transcribing = snap.phase === 'transcribing';
  const acquiring = snap.phase === 'acquiring';

  const stateLabel = acquiring
    ? 'Preparing'
    : asking
      ? 'Examiner is asking'
      : answering
        ? 'Your turn'
        : between
          ? 'Moving on'
          : transcribing
            ? 'Scoring your answers'
            : '';
  const labelClass = asking ? 'listen' : answering ? 'speak' : 'neutral';

  return (
    <section className="screen-session">
      <div className="sbar">
        <div className="loc">
          Part {snap.question?.part ?? 1} ·{' '}
          <b>
            {snap.total > 0 ? `Question ${Math.min(snap.index + 1, snap.total)} of ${snap.total}` : `Question ${snap.index + 1}`}
          </b>
        </div>
        <div className="timer">
          <span className="dot" style={{ opacity: answering ? 1 : 0 }} />
          {fmtClock(elapsed)}
        </div>
      </div>

      <div className="stagearea">
        <div className={`state-label ${labelClass}`}>{stateLabel}</div>

        {answering ? (
          <div className="wave" ref={waveRef} aria-hidden="true">
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} />
            ))}
          </div>
        ) : (
          <div className={`orb${asking ? ' animate' : ''}`} aria-hidden="true">
            <span className="ring" />
            <span className="core" />
          </div>
        )}

        {snap.revealed && snap.question ? (
          <div className="qtext">{snap.question.text}</div>
        ) : (
          <div className="qhidden">• • •</div>
        )}

        <div className="subnote">
          {acquiring
            ? 'Getting your microphone ready…'
            : asking
              ? 'The question is spoken, not shown. Listen, then answer.'
              : answering
                ? snap.question?.part === 2
                  ? 'This is the long turn — speak for up to two minutes. Press Next when you\'re done.'
                  : "Speak your answer. Press Next when you're done — or it moves on for you."
                : transcribing
                  ? 'One moment — transcribing and measuring your delivery.'
                  : ' '}
        </div>

        {snap.usingFallbackVoice && (asking || answering) && (
          <div className="fallback-note">
            Using a basic browser voice — pause and add a key in Settings for a natural examiner.
          </div>
        )}
      </div>

      <div className="controls">
        <button
          className={`btn-ghost${snap.revealed ? ' on' : ''}`}
          onClick={() => controller.toggleReveal()}
          disabled={!(asking || answering)}
        >
          {snap.revealed ? 'Hide question' : 'Show question'}
        </button>
        <button className="btn-ghost" onClick={() => controller.repeat()} disabled={!asking || snap.repeatsLeft <= 0}>
          {snap.repeatsLeft > 0 ? 'Repeat once' : 'Repeat used'}
        </button>
        <div className="spacer" />
        <button className="btn-ghost" onClick={() => controller.endSession()} title="End the session and see your review">
          End &amp; review
        </button>
        <button className="btn-next" onClick={() => controller.userNext()} disabled={!answering}>
          Next question
        </button>
      </div>
      <div className="vstr">v{APP_VERSION}</div>
    </section>
  );
}
